import React, { useState, useRef, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Select, { components } from 'react-select';
import styles from './styles';

// Renders each option as a checkbox row (replaces react-multiselect-checkboxes,
// which had no React 18 release). Built on react-select, already a dependency.
function CheckboxOption(props) {
  return (
    <components.Option {...props}>
      <input type="checkbox" checked={props.isSelected} onChange={() => null} style={styles.optionCheckbox} />
      <span>{props.label}</span>
    </components.Option>
  );
}

/**
 * Mirrors the old getDropdownButtonLabel: placeholder when empty,
 * the single label when one item, otherwise "N selected".
 */
function buttonLabel(selectedValues, placeholder) {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
    return placeholder;
  }
  if (selectedValues.length === 1) {
    return selectedValues[0].label;
  }
  return `${selectedValues.length} selected`;
}

// Search box at the top of the dropdown (the control itself stays a button via
// isSearchable={false}). The input value and close handler are passed through
// selectProps; the component is defined at module scope so React keeps the same
// component type across renders — otherwise every keystroke would remount it and
// the input would lose focus.
function Menu(menuProps) {
  const { inputValue, onSearch, onClose } = menuProps.selectProps;
  return (
    <components.Menu {...menuProps}>
      <input
        autoFocus
        type="text"
        value={inputValue}
        placeholder="Search…"
        style={styles.searchInput}
        onChange={(e) => onSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        onMouseDown={(e) => e.stopPropagation()}
      />
      {menuProps.children}
    </components.Menu>
  );
}

// Always show the dropdown-button label rather than rendering selected chips in
// the control. Module scope keeps the component type stable across renders; the
// value and placeholder are read from selectProps.
function ValueContainer({ children, ...valueProps }) {
  const { value, placeholder } = valueProps.selectProps;
  return (
    <components.ValueContainer {...valueProps}>
      <span style={styles.buttonLabel}>{buttonLabel(value, placeholder)}</span>
      {React.Children.map(children, (child) => (child && child.type === components.Placeholder ? null : child))}
    </components.ValueContainer>
  );
}

function MultiSelect(props) {
  const { label, options = [], selectedValues = [], placeholder, onChange, onRemove, hasFilterOption } = props;
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const rootRef = useRef(null);

  function closeMenu() {
    setMenuIsOpen(false);
    setInputValue('');
  }

  // Close the menu (and clear the search) when clicking outside the control.
  // react-select's own blur-close is bypassed because focusing the in-menu
  // search input would otherwise close the menu immediately.
  useEffect(() => {
    if (!menuIsOpen) return undefined;
    function handleClickOutside(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        closeMenu();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuIsOpen]);

  function handleOnChange(selected) {
    if (onChange) {
      onChange(selected || []);
    }
  }

  function handleRemove(e) {
    e.preventDefault();
    onRemove(e);
  }

  const handleFilterOption = (candidate, input) => {
    if (selectedValues?.find((f) => f.value === candidate.value)) return true;
    return candidate.data.__isNew__ || candidate.label?.toLowerCase().includes(input?.toLowerCase());
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      ...styles.dropdownButton,
      minHeight: 'auto',
      minWidth: 175,
      justifyContent: 'space-between',
    }),
    valueContainer: (base) => ({ ...base, padding: 0, display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }),
    indicatorsContainer: (base) => ({ ...base, alignItems: 'center' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
    option: (base, state) => ({
      ...base,
      cursor: 'pointer',
      color: 'inherit',
      backgroundColor: state.isSelected ? styles.optionSelected : state.isFocused ? styles.optionHover : 'transparent',
      ':active': { backgroundColor: styles.optionActive },
    }),
    menu: (base) => ({ ...base, ...styles.container }),
    indicatorSeparator: () => ({ display: 'none' }),
  };

  return (
    <div style={styles.root} ref={rootRef}>
      <span style={styles.label}>{label}</span>
      <Select
        isMulti
        isClearable={false}
        isSearchable={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        controlShouldRenderValue={false}
        backspaceRemovesValue={false}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        inputValue={inputValue}
        onInputChange={() => null}
        onSearch={setInputValue}
        onClose={closeMenu}
        placeholder={placeholder}
        options={options}
        value={selectedValues}
        onChange={handleOnChange}
        components={{ Option: CheckboxOption, ValueContainer, Menu }}
        styles={selectStyles}
        filterOption={hasFilterOption ? handleFilterOption : undefined}
      />
      <CloseIcon style={styles.clearIcon} onClick={handleRemove} />
    </div>
  );
}

export default MultiSelect;
